import React, { useCallback, useState, useEffect } from 'react';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import PropTypes from 'prop-types';
import FingoDialog from './FingoDialog';
import SelectDaysButtons from '../buttons/SelectDaysButtons';
import {
  DEFAULT_CALENDAR_MIN_DAYS_FROM_TODAY,
  daysFromTodayToDate,
  MAX_DATE_TO_PAY,
} from '../../constants/dates';
import { useCartDateDisableCheck } from '../../hooks/useUpcomingHolidays';
import GridDateButton from '../buttons/GridDateButton';
import OnboardingDatePicker from '../datePickers/OnboardingDatePicker';

const daysButtons = [
  {
    id: 'button-30-days',
    days: 30,
    label: '30',
  },
  {
    id: 'button-60-days',
    days: 60,
    label: '60',
  },
  {
    id: 'button-90-days',
    days: 90,
    label: '90',
  },
];

const OTHER_INPUT_SELECTED = -1;

function isNotManualSelection(selectedDays, daysToPay) {
  return selectedDays !== OTHER_INPUT_SELECTED && daysToPay !== null && daysToPay !== 0;
}

const SelectDatesForAllDialog = ({
  open,
  handleClose,
  isMobile,
  onAccept,
  useSelectDaysButtons,
  minDaysFromToday,
}) => {
  const shouldDisableDate = useCartDateDisableCheck();
  const [isClosingByAccept, setIsClosingByAccept] = useState(false);
  const formatLabelDays = useCallback(
    (slideDays) => (slideDays === 0 || slideDays === null ? '' : `${slideDays}`),
    [],
  );
  const [selectedDays, setSelectedDays] = useState(null);
  const [daysToPay, setDaysToPay] = useState(null);
  const [dateValue, setDateValue] = useState(null);
  const datePickerOnChange = (newValue) => {
    setDateValue(newValue);
  };
  useEffect(() => {
    if (isNotManualSelection(selectedDays, daysToPay)) {
      const newDate = daysFromTodayToDate(daysToPay);
      datePickerOnChange(newDate);
    }
  }, [daysToPay, selectedDays]);

  const handleAccept = () => {
    setIsClosingByAccept(true);
    onAccept(dateValue);
  };

  const handleDialogClose = () => {
    if (!isClosingByAccept) {
      handleClose();
    }
    setIsClosingByAccept(false);
  };

  const pickerMinDate = daysFromTodayToDate(minDaysFromToday);

  const GridDateButtonCallback = useCallback(({
    setOpen,
    value: buttonValue,
    inputProps,
    error,
    inputRef,
    InputProps,
    ...buttonParams
  }) => (
    <GridDateButton
      setOpen={setOpen}
      value={buttonValue}
      {...buttonParams}
    />
  ), []);

  return (
    <FingoDialog
      titleBackgroundColor="white"
      handleClose={handleDialogClose}
      title={(
        <Typography
          color="gray.A700"
          textAlign="center"
          variant="large_1"
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
            textAlign: 'center',
            px: 2.5,
          }}
        >
          Seleccionar misma fecha para todos los deudores
        </Typography>
      )}
      subtitle={(
        <Typography
          variant={isMobile ? 'small_2' : 'medium_1'}
          color="gray.A500"
          textAlign="center"
          sx={{
            mb: { xs: 1, md: 2 },
            px: { xs: 1, md: 2 },
            py: 0,
            display: 'flex',
            justifyContent: 'center',
            width: '100%',
          }}
        >
          Elige el plazo de pago que quieras aplicar a todos tus deudores.
        </Typography>
      )}
      paddingContent={0}
      titleColor="primary.main"
      open={open}
    >
      <Stack sx={{ p: 2, display: 'flex', justifyContent: 'center', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
        <Grid
          container
          columnSpacing={1}
          rowSpacing={0}
          id="select-days-buttons-cell"
          sx={{
            width: '280px',
          }}
        >
          {useSelectDaysButtons ? (
            <SelectDaysButtons
              daysButtons={daysButtons}
              selectedDays={selectedDays}
              setSelectedDays={setSelectedDays}
              daysToPay={daysToPay}
              setDaysToPay={setDaysToPay}
              formatLabelDays={formatLabelDays}
              maxDaysToPay={120}
              showButtonSubtitle={!isMobile}
              showDatePicker
              datePickerProps={{
                maxDate: MAX_DATE_TO_PAY,
                minDate: pickerMinDate,
                value: dateValue,
                onChange: datePickerOnChange,
                shouldDisableWeekend: shouldDisableDate,
              }}
              buttonProps={{
                sx: {
                  width: { xs: 45, md: 50 },
                  minWidth: { xs: 45, md: 50 },
                  p: 0,
                },
              }}
              rowProps={{
                sx: {
                  height: { xs: 35, md: 50 },
                },
              }}
              useSmallDaysButtons
            />
          ) : (
            <OnboardingDatePicker
              value={dateValue}
              onChange={datePickerOnChange}
              minDate={pickerMinDate}
              maxDate={MAX_DATE_TO_PAY}
              shouldDisableWeekend={shouldDisableDate}
              ButtonComponent={GridDateButtonCallback}
            />
          )}
        </Grid>
        <Button
          variant="contained"
          color="primary"
          sx={{ width: 200 }}
          disabled={!dateValue}
          onClick={handleAccept}
        >
          <Typography variant="small_3" color="white" fontWeight={600}>
            Aceptar
          </Typography>
        </Button>
      </Stack>
    </FingoDialog>
  );
};

SelectDatesForAllDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  isMobile: PropTypes.bool.isRequired,
  onAccept: PropTypes.func.isRequired,
  useSelectDaysButtons: PropTypes.bool.isRequired,
  minDaysFromToday: PropTypes.number,
};

SelectDatesForAllDialog.defaultProps = {
  minDaysFromToday: DEFAULT_CALENDAR_MIN_DAYS_FROM_TODAY,
};

export default SelectDatesForAllDialog;
